import { computed, defineComponent, h, ref, watch } from 'vue';
import { loader, message, pageSubtitle } from '@/ui/components';
import InspectionReport from '@/ui/components/inspectionReport/InspectionReport';
import { Modal, modalProps, ModalProps } from '@/ui/components/Modal';
import { useTask } from '@/ui/concerns';
import { get } from '@/services/api/resources/inspection';
import { inspectionDetailPageLink } from '@/ui/helpers/inspectionHelper';
import { legacyRecord } from '@/entities/inspection/model';
import { VNodeData } from 'vue/types/vnode.d';
import { t } from '@/ui/i18n/i18n';
import { isFormTranslationsFeatureEnabled, user } from '@/ui/auth/auth';
import { filterListToOptions, sequence } from '@/ui/helpers';
import { filter as getAllCategories } from '@/services/api/resources/category';
import { SelectOptions } from '@/ui/types';

interface InspectionReportModalProps extends ModalProps {
  inspectionId: number;
}

export const InspectionReportModal = defineComponent<InspectionReportModalProps>({
  name: 'InspectionReportModal',
  props: [...modalProps, 'inspectionId'],
  setup(props, context) {
    const categories = ref([] as SelectOptions);
    const record = ref<legacyRecord>({
      inspection_id: null,
      pdf_url: null,
      photos_url: null,
      activities_count: 0,
      authorized_to_edit: false,
      fullTranslationLanguages: [],
    });

    // get record
    const getRecord = useTask(
      () => sequence(
        get(props.inspectionId),
        getAllCategories(),
      ),
      ([data, categoriesResponse]) => {
        record.value = data.parsed as legacyRecord;
        categories.value = filterListToOptions(categoriesResponse);
      },
    );

    const onModalClose = () => context.emit('modal:close');

    const onModalToggle = (active: boolean) => {
      if (active) {
        getRecord.run();
      }
    };

    const showTranslations = computed(() => {
      return isFormTranslationsFeatureEnabled.value
        && !!user.value.formTranslationsLocale
        && record.value.fullTranslationLanguages?.length;
    });

    watch(() => props.active, onModalToggle);

    return function render() {
      return h(Modal, {
        props: {
          customClass: 'inspection-report-modal',
          active: props.active,
          hasPrimaryBackgroundColor: true,
          trapFocus: true,
          canCancel: ['x'],
        },
        on: { close: onModalClose },
      }, [
        pageSubtitle(t('inspections.report'), 'inspection-report-modal__title'),
        h('div', { class: 'inspection-report-modal__inspection-link' }, [
          inspectionDetailPageLink(t('inspections.go-to-report'), props.inspectionId),
        ]),
        loader('div', getRecord, [
          h('div', { slot: 'error' }, [message('error', [t('inspections.not-available')])]),
          h(InspectionReport, {
            props: {
              resource: record.value,
              categories: categories.value,
              isLowQualityImages: user.value.organization.reportLowQualityImagesEnabled,
              isTranslationEnabled: showTranslations.value,
            },
          }),
        ], true, 'inspection-report-modal__loader'),
      ]);
    };
  },
});

export function inspectionReportModal(data: VNodeData & { props: InspectionReportModalProps }) {
  return h(InspectionReportModal, data);
}
